<template>
  <div
    class="flex h-full max-h-full flex-col gap-2 overflow-hidden rounded-lg bg-white p-6 dark:bg-slate-700"
  >
    <template v-if="dashboardListFiltered.length === 0">
      <SelectPublicDashboard
        :type-dashboard="typeDashboard"
        :go-to-new-dashboard="goToNewDashboard"
        @new-dashboard="newDashboard"
      />
    </template>
    <template v-else>
      <div class="flex flex-row items-baseline">
        <div class="min-w-0 flex-1 pr-2">
          <DashboardTabs
            v-model:selected-dashboard="selectedDashboard"
            :dashboard-list="dashboardListFiltered"
            :show-predefined-section="isAnalyticsDashboard"
            @action="handleDashboardTabAction"
            @selected="handleSelectedDashboard"
            @new-dashboard="newDashboard"
            @manage-dashboards="manageDashboards"
          />
        </div>
        <div v-if="$slots.cta" class="shrink-0 border-l-2 pl-2">
          <slot name="cta" />
        </div>
      </div>
      <div
        v-if="selectedDashboard"
        :key="rerenderKey"
        class="flex min-h-0 flex-1 flex-col gap-2"
      >
        <AnalyticsContent
          v-if="selectedDashboard.type === 'analytics'"
          :current-board="selectedDashboard"
        />
        <StatisticsContent
          v-if="selectedDashboard.type === 'statistics'"
          :current-board="selectedDashboard"
        />
        <AgendaContent
          v-else-if="selectedDashboard.type === 'agenda'"
          :current-board="selectedDashboard"
        />
        <PlanningContent
          v-else-if="selectedDashboard.type === 'planning'"
          :current-board="selectedDashboard"
        />
        <SupplierOrderContent
          v-else-if="selectedDashboard.type === 'supplier_order'"
          :current-board="selectedDashboard"
        />
        <DossierContent
          v-else-if="selectedDashboard.type === 'dashboard'"
          :current-board="selectedDashboard"
        />
        <PaymentContent
          v-else-if="selectedDashboard.type === 'payment'"
          :current-board="selectedDashboard"
        />
        <InvoicingContent
          v-else-if="
            [
              'invoice',
              'purchase_order',
              'quote',
              'credit_note',
              'invoice_proforma'
            ].includes(selectedDashboard.type)
          "
          :current-board="selectedDashboard"
        />
        <ThirdPartyCostContent
          v-else-if="selectedDashboard.type === 'invoice_item_third_party_cost'"
          :current-board="selectedDashboard"
        />
        <TaskComponent
          v-else-if="selectedDashboard.type === 'task'"
          :current-board="selectedDashboard"
        />
      </div>
    </template>
  </div>
  <DashboardModal
    :open="modalOpened"
    :dashboard-id="modalId"
    :action="modalAction"
    :type-dashboard-available="typeDashboard"
    @close="modalOpened = false"
    @dashboard-changed="rerenderDashboard"
  />
  <ManageDashboardModal
    :type-dashboard-available="typeDashboard"
    :open="manageModalOpened"
    @close="manageModalOpened = false"
  />
</template>

<script>
import { mapGetters } from 'vuex'
import DashboardTabs from '@c/Dashboard/Components/DashboardTabs.vue'
import DashboardModal from '@c/Dashboard/Components/DashboardModal.vue'
import ManageDashboardModal from '@c/Dashboard/Components/ManageDashboardModal.vue'
import StatisticsContent from '@c/Dashboard/Content/Statistics/StatisticsContent.vue'
import AgendaContent from '@c/Dashboard/Content/Agenda/AgendaContent.vue'
import DossierContent from '@c/Dashboard/Content/Dossier/DossierContent.vue'
import InvoicingContent from '@c/Dashboard/Content/Invoicing/InvoicingContent.vue'
import SelectPublicDashboard from '@c/Dashboard/Components/SelectPublicDashboard.vue'
import PlanningContent from '@c/Dashboard/Content/Planning/PlanningContent.vue'
import SupplierOrderContent from '@c/Dashboard/Content/SupplierOrder/SupplierOrderContent.vue'
import DashboardTypes from '@/assets/enums/dashboard/DashboardTypes'
import AnalyticsContent from '@c/Dashboard/Content/Analytics/AnalyticsContent.vue'
import PaymentContent from '@c/Dashboard/Content/Payment/PaymentContent.vue'
import TaskComponent from '@c/Dashboard/Content/Task/TaskContent.vue'
import ThirdPartyCostContent from '@c/Dashboard/Content/ThirdPartyCost/ThirdPartyCostContent.vue'
import {
  getLastTab,
  resolveTargetTabId
} from '@/router/utils/lastTabStorage'

const LAST_TAB_STORAGE_KEYS = {
  dashboard_page_route: 'dashboard:last_tab_id',
  dashboard_dossier_page_route: 'dashboard_dossier:last_tab_id'
}

export default {
  name: 'DashboardMainContainer',
  components: {
    TaskComponent,
    PaymentContent,
    ThirdPartyCostContent,
    AnalyticsContent,
    SupplierOrderContent,
    PlanningContent,
    SelectPublicDashboard,
    InvoicingContent,
    DossierContent,
    AgendaContent,
    StatisticsContent,
    DashboardModal,
    ManageDashboardModal,
    DashboardTabs
  },
  props: {
    typeDashboard: {
      type: Array,
      required: false,
      default: null
    }
  },
  data() {
    return {
      rerenderKey: 0,
      modalAction: 'add',
      modalId: null,
      modalOpened: false,
      manageModalOpened: false,
      selectedDashboard: null
    }
  },
  computed: {
    ...mapGetters({
      auth: 'auth/getAuth',
      dashboardList: 'metadata/getTemplateDashboards'
    }),
    isAnalyticsDashboard() {
      if (Array.isArray(this.typeDashboard)) {
        return this.typeDashboard.includes('analytics')
      }
      return this.typeDashboard === 'analytics'
    },
    getDashboardTitle() {
      if (this.selectedDashboard) {
        return this.selectedDashboard?.name[this.$i18n.locale]
      }
      return '~ Dashboard ~'
    },
    dashboardListFiltered() {
      const sortCondition = (a, b) => {
        const posA = a.pivot?.position ?? null
        const posB = b.pivot?.position ?? null
        if (posA === null || posB === null) {
          return posA === null ? 1 : -1
        }
        return posA - posB
      }

      const sortLocalCondition = (a, b) => {
        const localPosA = a.pivot?.local_position ?? null
        const localPosB = b.pivot?.local_position ?? null
        if (localPosA === null || localPosB === null) {
          return localPosA === null ? 1 : -1
        }
        return localPosA - localPosB
      }

      // Predefined dashboards bypass the favorite filter for analytics only
      const filterCondition = (dashboard) =>
        (dashboard.type === 'analytics' && dashboard.is_default) ||
        dashboard.pivot?.favorite
      if (this.typeDashboard === null) {
        return this.dashboardList.filter(filterCondition).sort(sortCondition)
      }
      if (Array.isArray(this.typeDashboard)) {
        const dashboardList = this.dashboardList.filter((dashboard) =>
          this.typeDashboard.includes(dashboard.type)
        )

        if (this.typeDashboard.length === 1) {
          return dashboardList.sort(sortLocalCondition)
        } else {
          const onlyFavorite = dashboardList
            .filter(filterCondition)
            .sort(sortCondition)
          return onlyFavorite.length === 0
            ? dashboardList.sort(sortCondition)
            : onlyFavorite
        }
      }
      return this.dashboardList
        .filter((dashboard) => this.typeDashboard === dashboard.type)
        .filter(filterCondition)
        .sort(sortCondition)
    }
  },
  watch: {
    '$route.params.id': {
      handler(dashboard_id) {
        if (dashboard_id) {
          this.selectDashboardById(dashboard_id)
        }
      },
      deep: true,
      immediate: true
    },
    /**
     * dashboardList (metadata) can still be loading when the immediate route watcher
     * above first runs, leaving selectedDashboard unresolved. Once the filtered list is
     * populated (or changes, e.g. after a reorder), re-resolve it from the current route
     * id instead of guessing how long the fetch takes.
     */
    dashboardListFiltered(newList) {
      if (newList.length && this.$route.params?.id) {
        this.selectDashboardById(this.$route.params.id)
      }
    }
  },
  beforeMount() {
    if (this.$route.params?.id) {
      this.selectDashboardById(this.$route.params.id)
    } else if (this.dashboardListFiltered[0] && this.$route.meta.route_name) {
      const storageKey = LAST_TAB_STORAGE_KEYS[this.$route.meta.route_name]
      const lastTabId = storageKey ? getLastTab(storageKey) : null
      const targetId = resolveTargetTabId(
        this.dashboardListFiltered,
        lastTabId
      )

      this.$router.push({
        name: this.$route.meta.route_name,
        params: { id: targetId }
      })
    }
  },
  methods: {
    selectDashboardById(dashboard_id) {
      this.selectedDashboard =
        this.dashboardListFiltered.find(
          (dashboard) => dashboard.id === dashboard_id
        ) ?? this.dashboardListFiltered[0]
    },
    handleSelectedDashboard(dashboard_id) {
      this.selectedDashboard = this.dashboardList.find(
        (dashboard) => dashboard.id === dashboard_id
      )
    },
    handleDashboardTabAction({ type, dashboard_id }) {
      this.modalAction = type
      this.modalId = dashboard_id
      this.selectedDashboard = this.dashboardListFiltered.find(
        (dashboard) => dashboard.id === dashboard_id
      )
      this.modalOpened = true
    },
    newDashboard() {
      this.modalAction = 'new'
      this.modalId = null
      this.modalOpened = true
    },
    manageDashboards() {
      this.manageModalOpened = true
    },
    rerenderDashboard(dashboard_template_id, go_to_dashboard) {
      if (go_to_dashboard) {
        this.goToNewDashboard(dashboard_template_id)
      } else if (
        this.selectedDashboard &&
        this.selectedDashboard.id === dashboard_template_id
      ) {
        this.rerenderKey++
      }
    },
    goToNewDashboard(template_dashboard_id) {
      if (this.typeDashboard) {
        let route_template_dashboard = ''

        if (this.typeDashboard.length > 1) {
          // Go dashboard view
          route_template_dashboard = 'dashboard_page_route'
        } else if (this.typeDashboard.length === 1) {
          const dashboardType = this.typeDashboard[0]

          switch (dashboardType) {
            // Go dossier view
            case DashboardTypes.DASHBOARD:
              route_template_dashboard = 'dashboard_dossier_page_route'
              break
            // Go agenda view
            case DashboardTypes.AGENDA:
              route_template_dashboard = 'agenda_page_route_id'
              break
            // Go planning view
            case DashboardTypes.PLANNING:
              route_template_dashboard = 'planning_page_route_id'
              break
            // Go supplier order view
            case DashboardTypes.SUPPLIER_ORDER:
              route_template_dashboard = 'supplier_order_dashboard_route_id'
              break
            // Go analytics view
            case DashboardTypes.STATISTICS:
              route_template_dashboard = 'dashboard_statistics_route_id'
              break
            // Go analytics view
            case DashboardTypes.ANALYTICS:
              route_template_dashboard = 'dashboard_analytics_route_id'
              break
            // Go invoice default view
            case DashboardTypes.INVOICE:
              route_template_dashboard = 'invoice_index_route_id'
              break
            // Go invoice purchase order view
            case DashboardTypes.PURCHASE_ORDER:
              route_template_dashboard = 'purchase_order_index_route_id'
              break
            // Go invoice quote view
            case DashboardTypes.QUOTE:
              route_template_dashboard = 'quote_index_route_id'
              break
            // Go invoice credit note view
            case DashboardTypes.CREDIT_NOTE:
              route_template_dashboard = 'credit_note_index_route_id'
              break
            case DashboardTypes.PAYMENT:
              route_template_dashboard = 'payment_index_route_id'
              break
            case DashboardTypes.INVOICE_ITEM_THIRD_PARTY_COST:
              route_template_dashboard = 'third_party_cost_index_route_id'
              break
          }
        }

        if (route_template_dashboard) {
          this.$router.push({
            name: route_template_dashboard,
            params: {
              id: template_dashboard_id
            }
          })
        }
      }
    }
  }
}
</script>
